import { z } from "zod";

/**
 * Le brouillon local d'un texte de la console : la saisie que la console n'a pas encore
 * confirmee, gardee dans le navigateur pour qu'un telephone qui perd le reseau, ou une page
 * rechargee, ne perde rien. Ne pour le texte d'une double des Histoires (plan, partie 2,
 * decision 10), il vit ici depuis que les livres-jeux (#276) s'en servent pour la trame et le
 * texte d'une scene : chaque module en fabrique un, a son prefixe (`creerBrouillons`), et les
 * cles ne se melangent jamais.
 *
 * La base reste la seule verite. Le brouillon porte la version du texte sur laquelle il a ete
 * tape : repris, il part avec elle, et si le texte a change entre-temps, c'est le 409. Il ne
 * se propose que s'il differe du texte enregistre. Il porte aussi les textes partis sans
 * reponse (`incertains`) : si la console en avait enregistre un, son 409 est notre propre
 * ecriture, pas un conflit (`sauvegarde-texte.ts`).
 *
 * Une seule cle par texte, que les onglets d'un meme appareil partagent : chacun n'efface le
 * brouillon que s'il garde encore exactement le texte que la console vient de confirmer
 * (`effacerBrouillonSi`), jamais la saisie qu'un autre onglet y a posee depuis.
 *
 * Le navigateur peut refuser (stockage bloque, quota plein, navigation privee stricte) : chaque
 * lecture et chaque ecriture passent par un `try`, et l'ecran marche alors sans brouillon.
 */

/** Au plus autant de textes partis sans reponse gardes avec un brouillon. */
export const INCERTAINS_MAX = 5;

/** Au plus autant de saisies gardees par texte. */
export const SAISIES_GARDEES_MAX = 10;

const date = z.string().refine((valeur) => !Number.isNaN(Date.parse(valeur)));

export type Brouillon = {
  readonly texte: string;
  readonly version: number;
  readonly le: string;
  readonly incertains?: readonly string[] | undefined;
};

export type SaisieGardee = { readonly texte: string; readonly le: string };

export type Brouillons = {
  /** La cle du navigateur, une par texte. */
  readonly cleBrouillon: (id: number) => string;
  /** Ce que le navigateur a garde, lu sans confiance : tout ce qui n'a pas la forme compte pour rien. */
  readonly analyserBrouillon: (brut: string | null) => Brouillon | null;
  readonly lireBrouillon: (id: number) => Brouillon | null;
  /** Vrai quand le navigateur a garde le brouillon. */
  readonly ecrireBrouillon: (id: number, brouillon: Brouillon) => boolean;
  readonly effacerBrouillon: (id: number) => void;
  /**
   * Efface le brouillon seulement s'il garde exactement ce texte : un autre onglet sur le meme
   * texte a pu y poser sa propre saisie depuis. Un reste illisible, qui ne garde rien, s'efface.
   */
  readonly effacerBrouillonSi: (id: number, texte: string) => void;
  /**
   * Le brouillon a proposer a l'ouverture : celui qui differe du texte enregistre. Un brouillon
   * egal au texte (sa sauvegarde a abouti, la page s'est fermee avant de l'effacer) est efface
   * sans rien dire.
   */
  readonly brouillonAProposer: (id: number, enregistre: string) => Brouillon | null;
  /**
   * Les saisies qu'un 409 a laissees de cote : « Recharger » a pose le texte de la console dans
   * la zone, et chacune reste copiable. Elles ont leur propre cle, a cote du brouillon, qu'une
   * frappe suivante remplacerait : elles survivent au depart de la page et au rechargement, et
   * chacune ne s'efface qu'a son « Oublier ». Une liste, la plus recente d'abord : un second 409
   * avant « Oublier » ajoute sa saisie, il n'ecrase jamais la precedente.
   */
  readonly cleSaisieGardee: (id: number) => string;
  /** Les saisies gardees d'un texte, la plus recente d'abord ; rien de lisible compte pour rien. */
  readonly lireSaisiesGardees: (id: number) => SaisieGardee[];
  /**
   * Garde une saisie en tete de la liste (une saisie deja gardee au meme texte n'y est pas deux
   * fois). Vrai quand le navigateur l'a gardee.
   */
  readonly garderSaisie: (id: number, saisie: SaisieGardee) => boolean;
  /** Oublie cette saisie-la, et elle seule. */
  readonly oublierSaisie: (id: number, saisie: SaisieGardee) => void;
};

/**
 * Les brouillons d'un module : `prefixe` fait ses cles (`<prefixe>:brouillon:<id>`,
 * `<prefixe>:saisie-gardee:<id>`), `texteMax` borne ce qui se relit.
 */
export function creerBrouillons(prefixe: string, texteMax: number): Brouillons {
  const brouillonSchema = z.object({
    texte: z.string().max(texteMax),
    version: z.number().int().nonnegative(),
    le: date,
    incertains: z.array(z.string().max(texteMax)).max(INCERTAINS_MAX).optional(),
  });
  const saisieGardeeSchema = z.object({ texte: z.string().max(texteMax), le: date });
  const saisiesGardeesSchema = z.array(saisieGardeeSchema).max(SAISIES_GARDEES_MAX);

  const cleBrouillon = (id: number): string => `${prefixe}:brouillon:${id}`;
  const cleSaisieGardee = (id: number): string => `${prefixe}:saisie-gardee:${id}`;

  const analyserBrouillon = (brut: string | null): Brouillon | null => {
    if (brut === null) return null;
    let valeur: unknown;
    try {
      valeur = JSON.parse(brut);
    } catch {
      return null;
    }
    const lu = brouillonSchema.safeParse(valeur);
    return lu.success ? lu.data : null;
  };

  const lireBrouillon = (id: number): Brouillon | null => {
    try {
      return analyserBrouillon(window.localStorage.getItem(cleBrouillon(id)));
    } catch {
      return null;
    }
  };

  const ecrireBrouillon = (id: number, brouillon: Brouillon): boolean => {
    try {
      window.localStorage.setItem(cleBrouillon(id), JSON.stringify(brouillon));
      return true;
    } catch {
      return false;
    }
  };

  const effacerBrouillon = (id: number): void => {
    try {
      window.localStorage.removeItem(cleBrouillon(id));
    } catch {
      // Rien a effacer la ou rien n'a pu etre garde.
    }
  };

  const effacerBrouillonSi = (id: number, texte: string): void => {
    try {
      const garde = analyserBrouillon(window.localStorage.getItem(cleBrouillon(id)));
      if (garde !== null && garde.texte !== texte) return;
      window.localStorage.removeItem(cleBrouillon(id));
    } catch {
      // Rien a effacer la ou rien n'a pu etre garde.
    }
  };

  const brouillonAProposer = (id: number, enregistre: string): Brouillon | null => {
    const brouillon = lireBrouillon(id);
    if (brouillon === null) return null;
    if (brouillon.texte === enregistre) {
      effacerBrouillonSi(id, enregistre);
      return null;
    }
    return brouillon;
  };

  const lireSaisiesGardees = (id: number): SaisieGardee[] => {
    try {
      const brut = window.localStorage.getItem(cleSaisieGardee(id));
      if (brut === null) return [];
      const lu = saisiesGardeesSchema.safeParse(JSON.parse(brut));
      return lu.success ? lu.data : [];
    } catch {
      return [];
    }
  };

  const poserLesSaisies = (id: number, saisies: readonly SaisieGardee[]): boolean => {
    try {
      if (saisies.length === 0) window.localStorage.removeItem(cleSaisieGardee(id));
      else window.localStorage.setItem(cleSaisieGardee(id), JSON.stringify(saisies));
      return true;
    } catch {
      return false;
    }
  };

  const garderSaisie = (id: number, saisie: SaisieGardee): boolean => {
    const autres = lireSaisiesGardees(id).filter((autre) => autre.texte !== saisie.texte);
    return poserLesSaisies(id, [saisie, ...autres].slice(0, SAISIES_GARDEES_MAX));
  };

  const oublierSaisie = (id: number, saisie: SaisieGardee): void => {
    const restantes = lireSaisiesGardees(id).filter(
      (autre) => autre.texte !== saisie.texte || autre.le !== saisie.le,
    );
    poserLesSaisies(id, restantes);
  };

  return {
    cleBrouillon,
    analyserBrouillon,
    lireBrouillon,
    ecrireBrouillon,
    effacerBrouillon,
    effacerBrouillonSi,
    brouillonAProposer,
    cleSaisieGardee,
    lireSaisiesGardees,
    garderSaisie,
    oublierSaisie,
  };
}
